import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getDocs, collection, query, where, orderBy, doc, setDoc, addDoc, updateDoc, deleteDoc, Timestamp, getDoc, onSnapshot } from 'firebase/firestore';
import GastoFormModal from '../ui/GastoFormModal';
import { appId, db } from '../../App';
import { formatCurrency, formatDate, getSafeDate } from '../../utils';

const EGRESO_PARTIDAS = [
    "Sueldos", "Alimentación y Viáticos", "Combustible y Lubricantes",
    "Mensajeros", "Gastos de Transporte", "Publicidad y Mercadeo",
    "Útiles de Oficina", "Mantenimiento y Reparaciones", "Servicios",
    "Otros Gastos Operativos", "Anticipo de Sueldos", "Liquidaciones"
].sort();

function ControlFinancieroModule({ showModal, closeModal, userId, permissions }) {
    const canManageExpenses = permissions?.CONTROL_FINANCIERO?.manage_expenses;
    
    const [allPackages, setAllPackages] = useState([]);
    const [isAppLoading, setIsAppLoading] = useState(true);

    const [selectedYear, setSelectedYear] = useState('');
    const [selectedMonth, setSelectedMonth] = useState('');
    const [saldoAnterior, setSaldoAnterior] = useState(0);
    const [isSaldoEditable, setIsSaldoEditable] = useState(false);
    const [showCalcButton, setShowCalcButton] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [financialReport, setFinancialReport] = useState(null);
    const [egresosDelMes, setEgresosDelMes] = useState([]);
    const [isGastoModalOpen, setIsGastoModalOpen] = useState(false);
    const [currentGastoToEdit, setCurrentGastoToEdit] = useState(null);

    useEffect(() => {
        setIsAppLoading(true);
        const packagesCollectionPath = `artifacts/${appId}/packages`;
        const q = query(collection(db, packagesCollectionPath));

        const unsubscribe = onSnapshot(q, (querySnapshot) => {
            const packagesData = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
            }));
            setAllPackages(packagesData);
            setIsAppLoading(false);
        }, (error) => {
            console.error("Error al cargar todos los paquetes:", error);
            showModal("Error Crítico", "No se pudo cargar la base de datos de paquetes.");
            setIsAppLoading(false);
        });

        return () => unsubscribe();
    }, [showModal]);
    
    const years = useMemo(() => {
        if (allPackages.length === 0) return [new Date().getFullYear().toString()];
        const allYears = new Set(allPackages.map(p => p.shipDate ? p.shipDate.toDate().getFullYear() : null));
        const currentYear = new Date().getFullYear();
        allYears.add(currentYear);
        return Array.from(allYears).filter(Boolean).map(String).sort((a, b) => b.localeCompare(a));
    }, [allPackages]);

    const months = useMemo(() => {
        return Array.from({length: 12}, (e, i) => ({ value: String(i), name: new Date(2000, i, 1).toLocaleString('es-GT', { month: 'long' }) }));
    }, []);

    const calculateAllFinancials = useCallback(async (balanceToUse) => {
        if (!selectedYear || selectedMonth === '' || !db || !userId) return;
        setIsLoading(true);
        setFinancialReport(null);
    
        const monthStart = new Date(parseInt(selectedYear), parseInt(selectedMonth), 1);
        const monthEnd = new Date(parseInt(selectedYear), parseInt(selectedMonth) + 1, 0, 23, 59, 59, 999);
    
        const entregadosDelMes = allPackages.filter(pkg => {
            if (!pkg.status || pkg.status.toLowerCase() !== "entregado") return false;
            if (!pkg.shipDate) return false;
            const shipDateObj = pkg.shipDate instanceof Timestamp ? pkg.shipDate.toDate() : new Date(pkg.shipDate);
            if (isNaN(shipDateObj.getTime())) return false;
            return shipDateObj >= monthStart && shipDateObj <= monthEnd;
        });
    
        let newPackageTotals = { totalNetoGeneralPaquetes: 0, totalNetoBancoPaquetes: 0, totalNetoEfectivoPaquetes: 0, conteoPaquetes: 0, gastosPaquetesPequeño: 0, totalIngresoPorEnvios: 0 };
        entregadosDelMes.forEach(pkg => {
            const totalPaquete = (parseFloat(pkg.productValue) || 0) + (parseFloat(pkg.shippingCost) || 0);
            const gastosPaquete = parseFloat(pkg.expenses) || 0;
            const netoPaquete = totalPaquete - gastosPaquete;
            newPackageTotals.totalNetoGeneralPaquetes += netoPaquete;
            newPackageTotals.conteoPaquetes++;
            newPackageTotals.gastosPaquetesPequeño += gastosPaquete;
            newPackageTotals.totalIngresoPorEnvios += (parseFloat(pkg.shippingCost) || 0);
            const paymentMethodLower = pkg.paymentMethod ? pkg.paymentMethod.toLowerCase() : '';
            if (["transferencia", "pos", "link", "depósito", "deposito"].includes(paymentMethodLower)) {
                newPackageTotals.totalNetoBancoPaquetes += netoPaquete;
            } else if (paymentMethodLower === "efectivo") {
                newPackageTotals.totalNetoEfectivoPaquetes += netoPaquete;
            }
        });
    
        const monthYearStr = `${selectedYear}-${String(parseInt(selectedMonth) + 1).padStart(2, '0')}`;
        let currentTotalEgresos = 0;
        let gastosMensajerosGrande = 0;
        let fetchedEgresos = [];
        let egresosPartidaCalculado = {};
        EGRESO_PARTIDAS.forEach(partida => egresosPartidaCalculado[partida] = 0);
    
        const egresosRef = collection(db, `artifacts/${appId}/egresosGenerales`);
        const egresosQuery = query(egresosRef, where("monthYear", "==", monthYearStr), orderBy("fecha", "desc"));
        try {
            const querySnapshot = await getDocs(egresosQuery);
            fetchedEgresos = querySnapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data(), fecha: docSnap.data().fecha.toDate() }));
            setEgresosDelMes(fetchedEgresos);
    
            fetchedEgresos.forEach(egreso => {
                const montoEgreso = parseFloat(egreso.monto) || 0;
                currentTotalEgresos += montoEgreso;
                if (egreso.partida && egresosPartidaCalculado.hasOwnProperty(egreso.partida)) {
                    egresosPartidaCalculado[egreso.partida] += montoEgreso;
                }
                if (egreso.partida && egreso.partida.toLowerCase() === "mensajeros") {
                    gastosMensajerosGrande += montoEgreso;
                }
            });
        } catch (error) {
            console.error("Error fetching egresos:", error);
            showModal("Error al Cargar Egresos", `No se pudieron cargar los egresos: ${error.message || "Desconocido"}.`);
            setEgresosDelMes([]);
        }
    
        const saldoAnteriorNum = parseFloat(balanceToUse) || 0;
        const totalGastosMensajeros = newPackageTotals.gastosPaquetesPequeño + gastosMensajerosGrande;
        const gananciaMensajeria = newPackageTotals.totalIngresoPorEnvios - totalGastosMensajeros;
        const saldoFinalCalculado = saldoAnteriorNum + newPackageTotals.totalNetoEfectivoPaquetes - currentTotalEgresos;
    
        setFinancialReport({ ...newPackageTotals, gastosMensajerosGrande, totalGastosMensajeros, gananciaMensajeria, totalEgresosDelMes: currentTotalEgresos, saldoFinalEfectivo: saldoFinalCalculado, egresosPorPartida: egresosPartidaCalculado, isCalculated: true });
    
        // --- INICIO DE LÓGICA DE CORTE DE FECHA (GUARDADO) ---
        // Se define el año y mes de inicio del sistema. Junio 2025 (mes 5 en base 0).
        const goLiveYear = 2025;
        const goLiveMonth = 5; // 0=Enero, 1=Feb, ..., 5=Junio

        const isHistoricMonth = parseInt(selectedYear) < goLiveYear || 
                               (parseInt(selectedYear) === goLiveYear && parseInt(selectedMonth) < goLiveMonth);

        // Solo guardar el balance final calculado para el mes de inicio o meses futuros.
        // Esto evita que el sistema sobrescriba con "0" un saldo de un mes histórico
        // que fue introducido manualmente al visitar ese mes.
        if (!isHistoricMonth) {
            try {
                const monthId = `${selectedYear}-${String(parseInt(selectedMonth) + 1).padStart(2, '0')}`;
                const balanceDocRef = doc(db, `artifacts/${appId}/monthlyBalances`, monthId);
                await setDoc(balanceDocRef, { year: parseInt(selectedYear), month: parseInt(selectedMonth), finalCashBalance: saldoFinalCalculado, updatedAt: Timestamp.now() }, { merge: true });
            } catch (error) {
                console.error("Error al guardar saldo mensual:", error);
            }
        }
        // --- FIN DE LÓGICA DE CORTE DE FECHA ---

        setIsLoading(false);
    }, [allPackages, selectedYear, selectedMonth, db, userId, showModal]);

    useEffect(() => {
        if (years.length > 0 && !selectedYear) {
            setSelectedYear(years[0]);
        }
        if (selectedMonth === '') {
            setSelectedMonth(String(new Date().getMonth()));
        }

        const initializeAndCalculate = async () => {
            if (!selectedYear || selectedMonth === '' || !db || !userId) return;
            
            setIsLoading(true);
            setFinancialReport(null);
            setShowCalcButton(false);

            let prevBalance = 0;
            
            let prevMonth = parseInt(selectedMonth) - 1;
            let prevYear = parseInt(selectedYear);
            if (prevMonth < 0) { prevMonth = 11; prevYear -= 1; }
            
            const prevMonthId = `${prevYear}-${String(prevMonth + 1).padStart(2, '0')}`;
            const balanceDocRef = doc(db, `artifacts/${appId}/monthlyBalances`, prevMonthId);

            try {
                const docSnap = await getDoc(balanceDocRef);

                // --- INICIO DE LÓGICA DE CORTE DE FECHA (EDICIÓN) ---
                const goLiveYear = 2025;
                const goLiveMonth = 5; // 0=Enero, 1=Feb, ..., 5=Junio

                const isHistoricMonth = parseInt(selectedYear) < goLiveYear || 
                                       (parseInt(selectedYear) === goLiveYear && parseInt(selectedMonth) < goLiveMonth);

                if (docSnap.exists()) {
                    prevBalance = docSnap.data().finalCashBalance || 0;
                    setSaldoAnterior(prevBalance);
                    // Para meses históricos, el saldo SIEMPRE es editable para permitir correcciones.
                    // Para meses actuales/futuros, solo es editable si es cero, para proteger la data.
                    setIsSaldoEditable(isHistoricMonth ? true : prevBalance === 0);
                } else {
                    setSaldoAnterior(0);
                    // Si el documento de saldo no existe, siempre debe ser editable.
                    setIsSaldoEditable(true);
                }
                // --- FIN DE LÓGICA DE CORTE DE FECHA ---

                await calculateAllFinancials(prevBalance);
            } catch (error) {
                console.error("Error al cargar saldo del mes anterior:", error);
                showModal("Error", `No se pudo cargar el saldo del mes anterior. Se asumirá un valor de 0. Error: ${error.message}`);
                setSaldoAnterior(0);
                setIsSaldoEditable(true);
                setIsLoading(false);
            }
        };

        if(allPackages.length > 0) {
            initializeAndCalculate();
        }
    }, [selectedYear, selectedMonth, db, userId, years, calculateAllFinancials, showModal, allPackages]);

    const handleSaldoAnteriorChange = (e) => {
        setSaldoAnterior(e.target.value);
        if (isSaldoEditable) {
            setShowCalcButton(true);
        }
    };

    const handleRecalculateClick = async () => {
        setIsLoading(true);
        const newBalance = parseFloat(saldoAnterior) || 0;
        
        let prevMonth = parseInt(selectedMonth) - 1;
        let prevYear = parseInt(selectedYear);
        if (prevMonth < 0) { prevMonth = 11; prevYear -= 1; }
        
        const prevMonthId = `${prevYear}-${String(prevMonth + 1).padStart(2, '0')}`;
        const balanceDocRef = doc(db, `artifacts/${appId}/monthlyBalances`, prevMonthId);

        try {
            await setDoc(balanceDocRef, { finalCashBalance: newBalance, updatedAt: Timestamp.now() }, { merge: true });
            await calculateAllFinancials(newBalance);
            setShowCalcButton(false);
            
            // Se mantiene la lógica de bloqueo post-cálculo para meses nuevos
            const goLiveYear = 2025;
            const goLiveMonth = 5;
            const isHistoricMonth = parseInt(selectedYear) < goLiveYear || (parseInt(selectedYear) === goLiveYear && parseInt(selectedMonth) < goLiveMonth);
            if (!isHistoricMonth) {
                setIsSaldoEditable(false);
            }

        } catch (error) {
            console.error("Error al guardar saldo anterior y recalcular:", error);
            showModal("Error", "No se pudo guardar la corrección del saldo anterior.");
            setIsLoading(false);
        }
    };

    const handleSaveGasto = async (gastoData) => {
        if (!userId || !db) { showModal("Error", "Usuario o BD no disponible."); return; }
        setIsLoading(true);
        try {
            const egresosCollectionPath = `artifacts/${appId}/egresosGenerales`;
            const fechaEgreso = getSafeDate(gastoData.fecha);
            const dataToSave = { ...gastoData, fecha: Timestamp.fromDate(fechaEgreso), monthYear: `${fechaEgreso.getFullYear()}-${String(fechaEgreso.getMonth() + 1).padStart(2, '0')}`, monto: parseFloat(gastoData.monto), createdAt: Timestamp.now() };
            if (currentGastoToEdit) {
                const gastoDocRef = doc(db, egresosCollectionPath, currentGastoToEdit.id);
                await updateDoc(gastoDocRef, dataToSave);
                showModal("Éxito", "Egreso actualizado correctamente.");
            } else {
                await addDoc(collection(db, egresosCollectionPath), dataToSave);
                showModal("Éxito", "Egreso registrado correctamente.");
            }
            setIsGastoModalOpen(false);
            setCurrentGastoToEdit(null);
            await calculateAllFinancials(saldoAnterior);
        } catch (error) {
            console.error("Error al guardar egreso:", error);
            showModal("Error", `No se pudo guardar el egreso: ${error.message}`);
        }
        setIsLoading(false);
    };

    const handleDeleteGasto = (gastoId) => {
        const confirmDelete = async () => {
            closeModal();
            if (!db) { showModal("Error", "BD no disponible."); return; }
            setIsLoading(true);
            try {
                const egresoDocRef = doc(db, `artifacts/${appId}/egresosGenerales`, gastoId);
                await deleteDoc(egresoDocRef);
                showModal("Éxito", "Egreso eliminado correctamente.");
                await calculateAllFinancials(saldoAnterior);
            } catch (error) {
                console.error("Error al eliminar egreso:", error);
                showModal("Error", `No se pudo eliminar el egreso: ${error.message}`);
            }
            setIsLoading(false);
        };
        showModal("Confirmar Eliminación", "¿Estás seguro de que quieres eliminar este egreso? Esta acción no se puede deshacer.",
            <>
                <button onClick={confirmDelete} className="btn-primary mr-2 bg-red-600 hover:bg-red-800">Sí, Eliminar</button>
                <button onClick={closeModal} className="btn-secondary">Cancelar</button>
            </>
        );
    };
    
    if (!permissions?.CONTROL_FINANCIERO?.view) {
        return <div className="text-center py-10 bg-red-50 border border-red-200 rounded-lg shadow"><h2 className="text-2xl font-semibold text-red-700">Acceso Denegado</h2></div>;
    }
    
    if (isAppLoading) {
        return <div className="text-center py-20"><div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-sky-600 mx-auto mb-4"></div><p>Cargando base de datos de paquetes...</p></div>;
    }

    return (
        <div className="bg-white p-6 rounded-lg shadow-md">
            <h2 className="text-2xl font-semibold text-gray-800 mb-6">Control Financiero Mensual</h2>
            <div className="mb-6 grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                <div>
                    <label htmlFor="yearSelectFin" className="block text-sm font-medium text-gray-700 mb-1">Año:</label>
                    <select id="yearSelectFin" value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)} className="input-field bg-white w-full">
                        {years.map(y => <option key={y} value={y}>{y}</option>)}
                    </select>
                </div>
                <div>
                    <label htmlFor="monthSelectFin" className="block text-sm font-medium text-gray-700 mb-1">Mes:</label>
                    <select id="monthSelectFin" value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)} className="input-field bg-white w-full">
                        {months.map(m => <option key={m.value} value={m.value}>{m.name}</option>)}
                    </select>
                </div>
                <div>
                    <label htmlFor="saldoAnteriorInput" className="block text-sm font-medium text-gray-700 mb-1">Saldo Efectivo Mes Anterior (Q)</label>
                    <input type="number" id="saldoAnteriorInput" value={saldoAnterior} onChange={handleSaldoAnteriorChange} className="input-field w-full" placeholder="0.00" disabled={!isSaldoEditable || isLoading} />
                </div>
                {showCalcButton && (
                    <button onClick={handleRecalculateClick} className="btn-primary py-2.5 px-5 text-sm" disabled={isLoading}>
                        {isLoading ? "Calculando..." : "Calcular / Actualizar Vista"}
                    </button>
                )}
            </div>

            {isLoading && <p className="text-center text-sky-600 mt-6 animate-pulse">Cargando datos financieros...</p>}
            
            {!isLoading && financialReport && financialReport.isCalculated && (
                <>
                    <div className="mb-8 p-4 border rounded-lg bg-sky-50">
                        <h3 className="text-xl font-semibold text-sky-700 mb-3">Resumen del Mes Seleccionado</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            <div className="p-3 bg-white rounded shadow"><p className="text-sm text-gray-600">Total Neto (Paquetes Entregados):</p><p className="text-2xl font-bold">{formatCurrency(financialReport.totalNetoGeneralPaquetes)}</p></div>
                            <div className="p-3 bg-white rounded shadow"><p className="text-sm text-gray-600">Total Neto en Banco:</p><p className="text-2xl font-bold">{formatCurrency(financialReport.totalNetoBancoPaquetes)}</p></div>
                            <div className="p-3 bg-white rounded shadow"><p className="text-sm text-gray-600">Total Neto en Efectivo (Ingresos):</p><p className="text-2xl font-bold">{formatCurrency(financialReport.totalNetoEfectivoPaquetes)}</p></div>
                            <div className="p-3 bg-white rounded shadow"><p className="text-sm text-gray-600">Total Egresos del Mes (General):</p><p className="text-2xl font-bold text-red-600">{formatCurrency(financialReport.totalEgresosDelMes)}</p></div>
                            <div className="p-3 bg-green-100 rounded shadow col-span-1 md:col-span-2 lg:col-span-1"><p className="text-sm text-green-700">Saldo Final en Efectivo de este Mes:</p><p className="text-2xl font-bold text-green-700">{formatCurrency(financialReport.saldoFinalEfectivo)}</p><p className="text-xs text-gray-500">(Saldo Anterior + Neto Efectivo - Egresos)</p></div>
                        </div>
                    </div>
                    <div className="mb-8 p-4 border rounded-lg bg-amber-50">
                        <h3 className="text-xl font-semibold text-amber-700 mb-3">Resultados de Mensajería</h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="p-3 bg-white rounded shadow"><p className="text-sm text-gray-600">Total Ingreso por Envíos:</p><p className="text-xl font-bold">{formatCurrency(financialReport.totalIngresoPorEnvios)}</p></div>
                            <div className="p-3 bg-white rounded shadow"><p className="text-sm text-gray-600">Total Gastos Mensajeros:</p><p className="text-xl font-bold text-red-600">{formatCurrency(financialReport.totalGastosMensajeros)}</p><p className="text-xs text-gray-500">(G. Paquetes + G. Generales)</p></div>
                            <div className={`p-3 rounded shadow ${financialReport.gananciaMensajeria >= 0 ? 'bg-green-100' : 'bg-red-100'}`}><p className={`text-sm ${financialReport.gananciaMensajeria >= 0 ? 'text-green-700' : 'text-red-700'}`}>Resultado Mensajería (Ganancia/Pérdida):</p><p className={`text-xl font-bold ${financialReport.gananciaMensajeria >= 0 ? 'text-green-700' : 'text-red-700'}`}>{formatCurrency(financialReport.gananciaMensajeria)}</p></div>
                        </div>
                        <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="p-3 bg-white rounded shadow border border-orange-200"><p className="text-xs text-orange-600">Detalle: Gastos Paquetes (Pequeño)</p><p className="text-lg font-semibold text-orange-600">{formatCurrency(financialReport.gastosPaquetesPequeño)}</p></div>
                            <div className="p-3 bg-white rounded shadow border border-orange-200"><p className="text-xs text-orange-600">Detalle: G. Generales Mensajeros (Grande)</p><p className="text-lg font-semibold text-orange-600">{formatCurrency(financialReport.gastosMensajerosGrande)}</p></div>
                        </div>
                    </div>
                    <div className="mb-6">
                        <div className="flex justify-between items-center mb-3"><h3 className="text-xl font-semibold text-gray-700">Reporte de Egresos del Mes</h3>
                            {canManageExpenses && <button onClick={() => { setCurrentGastoToEdit(null); setIsGastoModalOpen(true); }} className="btn-success text-sm">+ Registrar Egreso</button>}
                        </div>
                        {egresosDelMes.length === 0 ? <p className="text-center text-gray-500">No hay egresos registrados para este mes.</p> : (
                            <div className="overflow-x-auto mb-6">
                                <table className="min-w-full table-auto border-collapse border border-gray-300">
                                    <thead className="bg-gray-100"><tr>{["Fecha", "Descripción", "Partida", "Monto (Q)", "Observaciones", "Acciones"].map(h =><th key={h} className="p-2 border border-gray-300 text-left text-sm font-semibold text-gray-600">{h}</th>)}</tr></thead>
                                    <tbody>
                                        {egresosDelMes.map(gasto => (
                                            <tr key={gasto.id} className="hover:bg-gray-50">
                                                <td className="p-2 border border-gray-300 text-sm">{formatDate(gasto.fecha)}</td>
                                                <td className="p-2 border border-gray-300 text-sm">{gasto.descripcion}</td>
                                                <td className="p-2 border border-gray-300 text-sm">{gasto.partida}</td>
                                                <td className="p-2 border border-gray-300 text-sm text-right">{formatCurrency(gasto.monto)}</td>
                                                <td className="p-2 border border-gray-300 text-sm">{gasto.observaciones}</td>
                                                <td className="p-2 border border-gray-300 text-sm text-center">
                                                    <div className="flex justify-center space-x-2">
                                                        {canManageExpenses && <>
                                                            <button onClick={() => { setCurrentGastoToEdit(gasto); setIsGastoModalOpen(true);}} className="text-sky-600 hover:text-sky-800 p-1 rounded hover:bg-sky-100" title="Editar Egreso"><svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor"><path d="M17.414 2.586a2 2 0 00-2.828 0L7 10.172V13h2.828l7.586-7.586a2 2 0 000-2.828z" /><path fillRule="evenodd" d="M2 6a2 2 0 012-2h4a1 1 0 010 2H4v10h10v-4a1 1 0 112 0v4a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" clipRule="evenodd" /></svg></button>
                                                            <button onClick={() => handleDeleteGasto(gasto.id)} className="text-red-500 hover:text-red-700 p-1 rounded hover:bg-red-100" title="Eliminar Egreso"><svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" /></svg></button>
                                                        </>}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot className="bg-gray-100 font-bold text-gray-800">
                                        <tr>
                                            <td colSpan="3" className="p-2 border-l border-r border-gray-300 text-right">Total Egresos:</td>
                                            <td className="p-2 border-l border-r border-gray-300 text-right">{formatCurrency(financialReport.totalEgresosDelMes)}</td>
                                            <td colSpan="2" className="p-2 border-l border-r border-gray-300"></td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        )}
                    </div>
                     <div className="mt-8 p-4 border rounded-lg bg-blue-50">
                        <h3 className="text-xl font-semibold text-blue-700 mb-3">Análisis Detallado de Egresos del Mes</h3>
                        <div className="mb-2 p-2 bg-blue-100 rounded"><span className="font-semibold">Total Egresos del Mes (Suma de Partidas):</span> {formatCurrency(financialReport.totalEgresosDelMes)}</div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
                            <div><h4 className="font-semibold text-gray-600 mb-2">Total por Partida:</h4><div className="space-y-1 text-sm max-h-60 overflow-y-auto pr-2">{Object.entries(financialReport.egresosPorPartida || {}).map(([partida, total]) => ((total > 0 || EGRESO_PARTIDAS.includes(partida)) && <div key={partida} className="flex justify-between py-1 border-b border-blue-100"><span>{partida}:</span><span className="font-medium">{formatCurrency(total)}</span></div>))}</div></div>
                            <div className="space-y-4"><div className="p-3 bg-white rounded shadow"><p className="text-sm text-gray-600">Total de Movimientos (Egresos):</p><p className="text-lg font-bold">{egresosDelMes.length}</p></div><div className="p-3 bg-white rounded shadow"><p className="text-sm text-gray-600">Promedio por Gasto (Egreso):</p><p className="text-lg font-bold">{egresosDelMes.length > 0 ? formatCurrency(financialReport.totalEgresosDelMes / egresosDelMes.length) : formatCurrency(0)}</p></div></div>
                        </div>
                    </div>
                </>
            )}
            
            {!isLoading && !financialReport && <p className="text-center text-gray-500 py-10">Selecciona un período para ver el reporte.</p>}
            
            {isGastoModalOpen && (
                <GastoFormModal isOpen={isGastoModalOpen} onClose={() => setIsGastoModalOpen(false)} onSave={handleSaveGasto}
                    currentGasto={currentGastoToEdit} isLoading={isLoading} showAppModal={showModal} />
            )}
        </div>
    );
}

export default ControlFinancieroModule;